
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as harvestService from '@/services/harvestService';
import type { Harvest } from '@/types/database.types';
import { HarvestFormValues } from '@/components/harvests/form/schema';

export const useGetHarvests = () => {
  return useQuery({
    queryKey: ['harvests'],
    queryFn: harvestService.getHarvests
  });
};

export const useGetHarvestById = (id: string) => {
  return useQuery({
    queryKey: ['harvests', id],
    queryFn: () => harvestService.getHarvestById(id),
    enabled: !!id
  });
};

export const useGetHarvestsByFarmId = (farmId: string) => {
  return useQuery({
    queryKey: ['harvests', 'farm', farmId],
    queryFn: () => harvestService.getHarvestsByFarmId(farmId),
    enabled: !!farmId
  });
};

export const useCreateHarvest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: Partial<HarvestFormValues>) => {
      // Create a clean copy of the data without the alternative fields
      const processedData = { ...data };
      
      // Make sure expected_date is set (either from starting_date or as is)
      if ('starting_date' in data && !data.expected_date) {
        processedData.expected_date = data.starting_date;
      }
      
      // Remove the alternative fields that aren't in the Harvest type
      if ('starting_date' in processedData) {
        delete processedData.starting_date;
      }
      
      if ('ending_date' in processedData) {
        delete processedData.ending_date;
      }
      
      // Ensure data matches the required Harvest type
      return harvestService.createHarvest(processedData as Omit<Harvest, 'id' | 'created_at' | 'updated_at'>);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['harvests'] });
      queryClient.invalidateQueries({ queryKey: ['harvests', 'farm', data.farm_id] });
    }
  });
};

export const useUpdateHarvest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<HarvestFormValues> }) => {
      // Create a clean copy of the data without the alternative fields
      const processedData = { ...data };
      
      // Make sure expected_date is set (either from starting_date or as is)
      if ('starting_date' in data && !data.expected_date) {
        processedData.expected_date = data.starting_date;
      }
      
      // Remove the alternative fields that aren't in the Harvest type
      if ('starting_date' in processedData) {
        delete processedData.starting_date;
      }
      
      if ('ending_date' in processedData) {
        delete processedData.ending_date;
      }
      
      // Ensure data matches the required Harvest type
      return harvestService.updateHarvest(id, processedData as Partial<Omit<Harvest, 'id' | 'created_at' | 'updated_at'>>);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['harvests'] });
      queryClient.invalidateQueries({ queryKey: ['harvests', data.id] });
      queryClient.invalidateQueries({ queryKey: ['harvests', 'farm', data.farm_id] });
    }
  });
};

export const useDeleteHarvest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: harvestService.deleteHarvest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['harvests'] });
    }
  });
};
